/**
 * Images through imgix: the same file, at the size the screen actually needs.
 *
 * imgix is a **proxy over the same bucket** — same paths, different host. So the
 * whole job is swapping `media.` for `imgix.` and appending a querystring:
 *
 *   origem       https://media.memberfy.dev/call4papers/banners/<hash>/<id>.jpg
 *   redimensiona https://imgix.memberfy.dev/…/<id>.jpg?w=400&auto=format,compress
 *
 * What the API stores and returns is always the **origin** URL. That is the
 * canonical address of the file, and tying the column to a CDN would mean a data
 * migration the day it changes. The size is a decision of whoever is drawing —
 * only the component knows whether this is an 80px avatar or a 1200px cover.
 *
 * Measured on a real 7.7 MB banner arriving whole into a 400px card:
 *
 *   origem                         7.761.920 bytes  jpeg
 *   ?w=400&auto=format,compress       17.860 bytes  webp — 435× menor
 *   ?w=800&auto=format,compress       67.266 bytes  webp
 *   ?w=1200&auto=format,compress     144.510 bytes  webp
 */

/**
 * Both hosts come from the environment. Nothing is guessed.
 *
 * Two earlier attempts got this wrong in the same way, by assuming the domain:
 * first deriving the proxy from `window.location`, then from a hardcoded
 * `media.memberfy.*` → `imgix.memberfy.*` table. **The platform runs white-label**
 * — a community answers on its own domain, which is neither `memberfy.dev` nor
 * `memberfy.net`. Any rule written against those names is a rule that silently
 * stops applying for every customer who brings their own.
 *
 * Bucket and proxy are **platform** settings, not per-community ones: every
 * tenant uploads to the same bucket and the keys are partitioned by community
 * id. So build-time env is the right shape — one build, many domains, one bucket.
 *
 * Unset means "serve from the origin", which is why this is safe to ship before
 * the CDN is provisioned: a missing or broken imgix must never mean a page with
 * no images.
 */
const trimSlash = (value: string) => value.replace(/\/+$/, '')

/**
 * What the server said, when it has said it.
 *
 * `GET /communities/domain` returns `media: { origin, imgix }`, and
 * `CommunityContext` hands it here. It wins over the build-time env for a
 * reason that matters to white-label: one build serves every domain, so a CDN
 * change through env means recompiling and redeploying the app. From the server
 * it is a config change.
 *
 * The env stays as the fallback — it is what answers before the community has
 * loaded, and what a preview environment can override with.
 */
let fromServer: { origin: string; imgix: string } | null = null

export const configureImages = (media?: { origin?: string | null; imgix?: string | null } | null) => {
  fromServer = media
    ? { origin: trimSlash(media.origin || ''), imgix: trimSlash(media.imgix || '') }
    : null
}

/** The host the API writes into the database. Only these files are ours. */
const mediaOrigin = (): string => {
  if (fromServer) return fromServer.origin
  const fromEnv = import.meta.env.VITE_MEDIA_URL
  return typeof fromEnv === 'string' ? trimSlash(fromEnv) : ''
}

/** The resizing proxy in front of that bucket. */
const imgixOrigin = (): string => {
  if (fromServer) return fromServer.imgix
  const fromEnv = import.meta.env.VITE_IMGIX_URL
  return typeof fromEnv === 'string' ? trimSlash(fromEnv) : ''
}

/** The media host, as a hostname, or null when nothing is configured. */
const mediaHostname = (): string | null => {
  const origin = mediaOrigin()
  if (!origin) return null
  try {
    return new URL(origin).hostname
  } catch {
    /* A malformed env is a configuration mistake, not a reason to stop drawing
       images — the origin URLs still work on their own. */
    return null
  }
}

/** Query parameters of a pre-signed URL (S3 SigV2/SigV4, CloudFront, GCS). */
const SIGNED_QUERY = /(?:^|[?&])(?:X-Amz-[A-Za-z]+|Signature|Expires|Key-Pair-Id|X-Goog-[A-Za-z]+)=/i

/** Formats imgix would damage: animation and vectors are served from the origin. */
const PASSTHROUGH_EXTENSIONS = /\.(?:gif|svg)$/i

export interface ImageOptions {
  /** Width in **CSS** pixels. `dpr` handles the retina multiplication. */
  w?: number
  /** Height in CSS pixels. Only meaningful together with `fit`. */
  h?: number
  /** `crop` fills `w`×`h` exactly; `clip` fits inside it. */
  fit?: 'crop' | 'clip' | 'max' | 'fill'
  /** What to keep when cropping. A face beats the middle of the picture. */
  crop?: string
  /** Device pixel ratio. 2 on retina, so `w` stays a CSS measurement. */
  dpr?: number
  /** Quality. Rarely worth touching while `auto=compress` is on. */
  q?: number
}

/**
 * The URL to actually render.
 *
 * Returns the input untouched whenever rewriting would be wrong or pointless:
 * an empty value, a `data:`/`blob:` preview of a file still being uploaded, a
 * seed image on Unsplash, or neither host configured.
 */
export const imageUrl = (
  url: string | null | undefined,
  options: ImageOptions = {}
): string | null | undefined => {
  if (!url) return url
  if (/^(data|blob):/i.test(url)) return url

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }

  /* Only files this platform uploaded. Handing imgix somebody else's URL asks
     it to fetch an origin it cannot read, and it would turn a working Unsplash
     seed image into a 404 on our own CDN. */
  if (parsed.hostname !== mediaHostname()) return url

  /* A signed link (private bucket, expiring URL) only works with its own
     querystring, and imgix reads the bucket without it. Dropping the signature
     would turn a working private image into a 403, so it is served as is. */
  if (parsed.search && SIGNED_QUERY.test(parsed.search)) return url

  /* imgix flattens an animated GIF to its first frame once it is resized or
     converted, and rasterizes an SVG — both lose what the file is for. */
  if (PASSTHROUGH_EXTENSIONS.test(parsed.pathname)) return url

  const base = imgixOrigin()
  if (!base) return url

  const params = new URLSearchParams()
  /* Always on: picks webp/avif from the browser's `Accept` and compresses.
     It is most of the saving, and there is no case where we want neither. */
  params.set('auto', 'format,compress')

  for (const [key, value] of Object.entries(options)) {
    if (value !== undefined && value !== null) params.set(key, String(value))
  }

  return `${base}${parsed.pathname}?${params.toString()}`
}

/**
 * A `srcset` for the same image at 1x and 2x.
 *
 * Cheaper than asking for `dpr=2` everywhere: a phone on a slow connection keeps
 * the small file, and the retina laptop gets the sharp one, decided by the
 * browser rather than by us guessing.
 */
export const imageSrcSet = (
  url: string | null | undefined,
  options: ImageOptions & { w: number }
): string | undefined => {
  const one = imageUrl(url, options)
  const two = imageUrl(url, { ...options, dpr: 2 })
  if (!one || !two || one === url) return undefined
  return `${one} 1x, ${two} 2x`
}

/**
 * Whether a URL is a file this platform serves: https on the media origin or
 * on the imgix proxy in front of it. Used to let `<img>` through the HTML
 * sanitizer only for our own files (imported and Substack posts have their
 * images re-hosted there), so a post body cannot make readers' browsers load
 * an arbitrary third-party URL.
 */
export const isPlatformMediaUrl = (url: string | null | undefined): boolean => {
  if (!url) return false
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return false
  const hosts = [mediaOrigin(), imgixOrigin()]
    .map((origin) => {
      try {
        return origin ? new URL(origin).hostname : null
      } catch {
        return null
      }
    })
    .filter((host): host is string => Boolean(host))
  return hosts.includes(parsed.hostname)
}

/**
 * Rich HTML (a post body, a status) with every platform image sent through
 * imgix, at the width of the column it is drawn in plus a 2x `srcset`.
 *
 * Done when rendering, never when saving: the stored HTML keeps the origin
 * URLs, like every other image column. The origin is kept on `data-origin`, so
 * the lightbox can open the full-size file rather than the column-sized one.
 * Images that are not ours, or that `imageUrl` would not rewrite, are left
 * untouched — the same rules as everywhere else.
 */
export const rewriteHtmlImages = (html: string, options: ImageOptions & { w: number }): string => {
  if (!html || !/<img\b/i.test(html) || typeof document === 'undefined') return html
  if (!imgixOrigin() || !mediaHostname()) return html

  const template = document.createElement('template')
  template.innerHTML = html
  let changed = false
  template.content.querySelectorAll('img').forEach((img) => {
    const original = img.getAttribute('src')
    const src = imageUrl(original, options)
    if (!original || !src || src === original) return
    img.setAttribute('data-origin', original)
    img.setAttribute('src', src)
    /* An explicit srcset from the editor would point at the origin again. */
    const srcSet = imageSrcSet(original, options)
    if (srcSet) img.setAttribute('srcset', srcSet)
    else img.removeAttribute('srcset')
    changed = true
  })
  return changed ? template.innerHTML : html
}
